/**
 * Logger unificado usando Pino
 * 
 * Substitui Winston por uma solução mais leve e performática.
 * Mantém a mesma interface para facilitar migração.
 * 
 * Funciona tanto no servidor quanto no client (com fallback para console no client).
 */

import pino from 'pino';

// Import trace context only on server (lazy import to avoid client bundle issues)
function getCurrentTraceContext(): { traceId?: string; spanId?: string; requestId?: string; userId?: string; companyId?: string } | undefined {
  if (typeof window !== 'undefined') {
    // Client-side: no trace context
    return undefined;
  }
  
  // Server-side: lazy import telemetry
  try {
    const telemetry = require('./telemetry');
    return telemetry.getCurrentTraceContext();
  } catch {
    // If telemetry fails to load, continue without it
    return undefined;
  }
}

// Helper function to recursively mask sensitive data in objects
function maskObject(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(maskObject);
  }

  if (obj && typeof obj === 'object') {
    const masked: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();

      // Check for sensitive field names
      if (
        lowerKey.includes('password') ||
        lowerKey.includes('secret') ||
        lowerKey.includes('token') ||
        lowerKey.includes('apikey') ||
        lowerKey.includes('cpf') ||
        lowerKey.includes('cnpj') ||
        lowerKey.includes('email') ||
        lowerKey.includes('phone')
      ) {
        masked[key] = '***';
      } else if (typeof value === 'string') {
        // Apply string masking
        let maskedValue = value;
        maskedValue = maskedValue.replace(/\d{3}\.\d{3}\.\d{3}-\d{2}/g, '***.***.***-**');
        maskedValue = maskedValue.replace(/\d{11}/g, '***********');
        maskedValue = maskedValue.replace(
          /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
          '***@***.***'
        );
        maskedValue = maskedValue.replace(/\(\d{2}\)\s?\d{4,5}-?\d{4}/g, '(**)****-****');
        maskedValue = maskedValue.replace(/\d{10,11}/g, '**********');
        maskedValue = maskedValue.replace(
          /(api[_-]?key|token|password|secret)["\s]*[:=]["\s]*[a-zA-Z0-9_-]+/gi,
          '$1="***"'
        );
        masked[key] = maskedValue;
      } else {
        masked[key] = maskObject(value);
      }
    }
    return masked;
  }

  return obj;
}

// Mask sensitive data in message string
function maskMessage(message: string): string {
  let maskedMessage = message;

  // Mask CPF (Brazilian SSN)
  maskedMessage = maskedMessage.replace(/\d{3}\.\d{3}\.\d{3}-\d{2}/g, '***.***.***-**');
  maskedMessage = maskedMessage.replace(/\d{11}/g, '***********');

  // Mask email
  maskedMessage = maskedMessage.replace(
    /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
    '***@***.***'
  );

  // Mask phone numbers
  maskedMessage = maskedMessage.replace(/\(\d{2}\)\s?\d{4,5}-?\d{4}/g, '(**)****-****');
  maskedMessage = maskedMessage.replace(/\d{10,11}/g, '**********');

  // Mask API keys and tokens
  maskedMessage = maskedMessage.replace(
    /(api[_-]?key|token|password|secret)["\s]*[:=]["\s]*[a-zA-Z0-9_-]+/gi,
    '$1="***"'
  );

  return maskedMessage;
}

// Create Pino logger instance (only on server, fallback to console on client)
const isServer = typeof window === 'undefined';

const pinoLogger = isServer
  ? pino({
      level: process.env.LOG_LEVEL || (process.env.NODE_ENV === 'production' ? 'info' : 'debug'),
      base: {
        service: 'app-topamais',
        environment: process.env.NODE_ENV || 'development',
        version: process.env.npm_package_version || '1.0.0',
      },
      formatters: {
        level: (label) => {
          return { level: label };
        },
      },
      transport:
        process.env.NODE_ENV === 'development'
          ? {
              target: 'pino-pretty',
              options: {
                colorize: true,
                translateTime: 'HH:MM:ss.l',
                ignore: 'pid,hostname',
                singleLine: false,
              },
            }
          : undefined,
    })
  : // Client-side: create a minimal logger that uses console
    ({
      error: (obj: unknown, msg?: string) => {
        if (msg) console.error(`[ERROR] ${msg}`, obj);
        else console.error('[ERROR]', obj);
      },
      warn: (obj: unknown, msg?: string) => {
        if (msg) console.warn(`[WARN] ${msg}`, obj);
        else console.warn('[WARN]', obj);
      },
      info: (obj: unknown, msg?: string) => {
        if (msg) console.info(`[INFO] ${msg}`, obj);
        else console.info('[INFO]', obj);
      },
      debug: (obj: unknown, msg?: string) => {
        if (process.env.NODE_ENV === 'development') {
          if (msg) console.debug(`[DEBUG] ${msg}`, obj);
          else console.debug('[DEBUG]', obj);
        }
      },
    } as pino.Logger);

// Helper to enrich log data with trace context and mask sensitive data
function enrichLogData(metadata?: unknown): Record<string, unknown> {
  const enriched: Record<string, unknown> = {};

  // Add trace context if available (only on server)
  const traceContext = getCurrentTraceContext();
  if (traceContext) {
    enriched.traceId = traceContext.traceId;
    enriched.spanId = traceContext.spanId;
    enriched.requestId = traceContext.requestId;

    if (traceContext.userId) {
      enriched.userId = traceContext.userId;
    }

    if (traceContext.companyId) {
      enriched.companyId = traceContext.companyId;
    }
  }

  // Add and mask metadata
  if (metadata) {
    const maskedMetadata = maskObject(metadata);
    if (typeof maskedMetadata === 'object' && maskedMetadata !== null) {
      Object.assign(enriched, maskedMetadata);
    }
  }

  return enriched;
}

// Create a child logger with additional context
export function createChildLogger(context: Record<string, unknown>) {
  return pinoLogger.child(maskObject(context) as Record<string, unknown>);
}

// Logger interface matching the old Winston interface
export const log = {
  error: (message: string, metadata?: unknown, error?: Error) => {
    const enriched = enrichLogData(metadata);
    const maskedMessage = maskMessage(message);

    if (error) {
      enriched.error = {
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    }

    pinoLogger.error(enriched, maskedMessage);
  },

  warn: (message: string, metadata?: unknown) => {
    const enriched = enrichLogData(metadata);
    const maskedMessage = maskMessage(message);
    pinoLogger.warn(enriched, maskedMessage);
  },

  info: (message: string, metadata?: unknown) => {
    const enriched = enrichLogData(metadata);
    const maskedMessage = maskMessage(message);
    pinoLogger.info(enriched, maskedMessage);
  },

  debug: (message: string, metadata?: unknown) => {
    const enriched = enrichLogData(metadata);
    const maskedMessage = maskMessage(message);
    pinoLogger.debug(enriched, maskedMessage);
  },

  http: (message: string, metadata?: unknown) => {
    const enriched = enrichLogData(metadata);
    const maskedMessage = maskMessage(message);
    pinoLogger.info({ ...enriched, http: true }, maskedMessage);
  },
};

// Export default logger instance
export default pinoLogger;

// Helper functions for compatibility with logger-loki interface
// Import request-context only on server
function getContext(): { requestId?: string; userId?: string; companyId?: string; startTime?: number } | undefined {
  if (typeof window !== 'undefined') {
    // Client-side: no context
    return undefined;
  }
  
  try {
    const requestContext = require('./request-context');
    return requestContext.getContext();
  } catch {
    return undefined;
  }
}

// Função auxiliar para logs de negócio com tracking de transação
export function logBusinessEvent(
  level: 'info' | 'warn' | 'error',
  eventType: string,
  message: string,
  context?: Record<string, unknown>,
  transactionId?: string
) {
  const enrichedContext: Record<string, unknown> = {
    ...context,
    eventType,
    transactionId: transactionId || getContext()?.requestId,
    isBusinessEvent: true,
  };

  switch (level) {
    case 'info':
      log.info(message, enrichedContext);
      break;
    case 'warn':
      log.warn(message, enrichedContext);
      break;
    case 'error':
      log.error(message, enrichedContext);
      break;
  }
}

// Logs assíncronos (fire-and-forget) - mesma interface do logger-loki
export const logAsync = {
  info: (message: string, context?: Record<string, unknown>) => {
    log.info(message, context);
  },
  error: (message: string, context?: Record<string, unknown>, error?: Error) => {
    log.error(message, context, error);
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    log.warn(message, context);
  },
  debug: (message: string, context?: Record<string, unknown>) => {
    log.debug(message, context);
  },
  business: logBusinessEvent,
};

// Stream para Morgan HTTP logger
export const stream = {
  write: (message: string) => {
    const logMessage = `HTTP: ${message.trim()}`;
    const ctx = getContext();

    log.info(logMessage, {
      component: 'http',
      requestId: ctx?.requestId,
      userId: ctx?.userId,
      companyId: ctx?.companyId,
    });
  },
};

// Export logger instance for compatibility
export const logger = {
  info: (message: string, context?: Record<string, unknown>) => {
    log.info(message, context);
  },
  error: (message: string, context?: Record<string, unknown>, error?: Error) => {
    log.error(message, context, error);
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    log.warn(message, context);
  },
  debug: (message: string, context?: Record<string, unknown>) => {
    log.debug(message, context);
  },
};
